import React, { useState, useCallback, useEffect } from 'react';
import {
  CheckCircleOutline,
  InfoOutlined,
} from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Paper,
  Grid,
  Chip,
  Tooltip,
  Divider
} from '@mui/material';
import { Link } from 'react-router-dom';

import AppTable from 'components/Shared/AppTable';
import AppTableDateTime from 'components/Shared/AppTableDateTime';

import BreadcrumbLayout from 'layouts/BreadcrumbLayout';

import { useTablePagination } from 'hooks';
import { usePrepaymentDetailContext } from 'contexts/PrepaymentDetailContext';
import {
  useLazyGetDDLShipmentListForPrepaymentQuery,
  useUpdateShipmentPriceMutation,
} from 'store/services/shipmentApi';

const PrepaymentShipmentList = () => {
  const { prepaymentDetail, refetch } = usePrepaymentDetailContext(); // Include refetch
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();

  const [selectedRows, setSelectedRows] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const [fetchShipmentDetails, { data, isLoading, isFetching }] =
    useLazyGetDDLShipmentListForPrepaymentQuery();
  const [updateShipmentPrice, { isLoading: isUpdating }] = useUpdateShipmentPriceMutation();

  const handleRowSelected = useCallback(({ selectedRows }) => {
    setSelectedRows(selectedRows);
  }, []);

  const handleConfirmClick = () => {
    const totalCharge = selectedRows.reduce((sum, row) => sum + (row.charge || 0), 0);
    const balance = prepaymentDetail?.balanceAmount || 0;
    if (totalCharge > balance) {
      setSnackbar({ open: true, message: 'Total selected amount exceeds available balance' });
      return;
    }
    if (selectedRows.length === 0) {
      setSnackbar({ open: true, message: 'No items selected' });
      return;
    }
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleConfirmTransaction = async () => {
    try {
      for (const row of selectedRows) {
        await updateShipmentPrice({
          shipmentId: row.shipment._id,
          priceId: row._id,
          paymentStatus: 'Paid',
          prepaymentId: prepaymentDetail._id,
        }).unwrap();
      }
      setSnackbar({ open: true, message: 'Transactions completed successfully' });
      // Refetch shipment list
      await fetchShipmentDetails({
        sourceModel: prepaymentDetail.sourceModel,
        sourceModelRef: prepaymentDetail.sourceModelRef._id,
        account: prepaymentDetail.account._id,
        entity: prepaymentDetail.entity._id,
        page: currentPage,
        limit: perPage,
      }).unwrap();
      // Refetch prepayment details to update balanceAmount
      await refetch();
      setSelectedRows([]);
      setClearSelectedRows(true); // Clear selections after transaction
    } catch (error) {
      setSnackbar({ open: true, message: error?.data?.message || 'Failed to complete transactions' });
    }
    handleDialogClose();
  };

  const handleSnackbarClose = () => {
    setSnackbar({ open: false, message: '' });
  };

  useEffect(() => {
    if (clearSelectedRows) {
      setClearSelectedRows(false); // Reset after clearing
    }
  }, [clearSelectedRows]);

  const totalSelectedCharge = selectedRows.reduce((sum, row) => sum + (row.charge || 0), 0);
  const balance = prepaymentDetail?.balanceAmount || 0;
  const isBalanceSufficient = totalSelectedCharge <= balance;

  const getDetailPath = (shipmentId) => {
    switch (prepaymentDetail?.sourceModel) {
      case 'VendorProfile':
      case 'Customer':
      case 'Contractor':
        return `/app/crm/shipment/${shipmentId}/detail`;
      default:
        return `/app/crm/shipment/${shipmentId}/detail`;
    }
  };

  const columns = [
    {
      name: 'Shipment ID',
      selector: (row) => row?.shipmentId,
      cell: (row) => (
        <Typography variant="body2" fontWeight="medium">
          {row?.shipmentId}
        </Typography>
      ),
      width: '150px',
    },
    {
      name: 'Fee Item',
      selector: (row) => row?.feeItem?.name || 'N/A',
      cell: (row) => (
        <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row?.feeItem?.name || 'N/A'}
        </Box>
      ),
    },
    {
      name: 'Charge',
      selector: (row) => row?.charge || 0,
      cell: (row) => (
        <Typography>
          Rs. {row?.charge?.toFixed(2) || '0.00'}
        </Typography>
      ),
      width: '120px',
    },
    {
      name: 'Payer',
      selector: (row) => row?.payer || 'N/A',
      cell: (row) => (
        <Chip
          label={row?.payer || 'N/A'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      name: 'Created At',
      selector: (row) => row?.createdAt,
      cell: (row) => <AppTableDateTime date={row?.createdAt} />,
      width: '150px',
    },
    {
      name: 'Actions',
      cell: (row) => (
        <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
          {row?.shipment?._id && (
            <Tooltip title="View Shipment Details" arrow>
              <Link to={getDetailPath(row.shipment._id)}>
                <IconButton size="small" aria-label="View Shipment" color="primary">
                  <InfoOutlined fontSize="small" />
                </IconButton>
              </Link>
            </Tooltip>
          )}
        </Box>
      ),
      ignoreRowClick: true,
      right: true,
      width: '80px',
    },
  ];

  useEffect(() => {
    if (
      !prepaymentDetail?._id ||
      !prepaymentDetail?.sourceModel ||
      !prepaymentDetail?.sourceModelRef?._id ||
      !prepaymentDetail?.account?._id ||
      !prepaymentDetail?.entity?._id
    ) {
      return;
    }
    fetchShipmentDetails({
      sourceModel: prepaymentDetail.sourceModel,
      sourceModelRef: prepaymentDetail.sourceModelRef._id,
      account: prepaymentDetail.account._id,
      entity: prepaymentDetail.entity._id,
      page: currentPage,
      limit: perPage,
    });
  }, [currentPage, fetchShipmentDetails, perPage, prepaymentDetail]);

  return (
    <>
      <BreadcrumbLayout.Paper>
        <Box className="p-4 sm:p-6">
          <Grid container spacing={2} alignItems="center" className="mb-4">
            <Grid item xs={12} md={6}>
            </Grid>
            <Grid item xs={12} md={6} className="flex justify-end">
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirmClick}
                disabled={selectedRows.length === 0 || isUpdating}
                className="shadow-md hover:shadow-lg transition-shadow"
              >
                Pay Prepayment ({selectedRows.length})
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={2} className="mb-6">
            <Grid item xs={12} sm={4}>
              <Paper className="p-4 rounded-lg border border-gray-100 shadow-sm">
                <Typography variant="subtitle2" color="textSecondary" className="flex items-center gap-1">
                  Available Balance
                </Typography>
                <Typography variant="h5" fontWeight="bold" className="mt-1">
                  Rs. {balance?.toFixed(2) || '0.00'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper className="p-4 rounded-lg border border-gray-100 shadow-sm">
                <Typography variant="subtitle2" color="textSecondary">
                  Selected Amount
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  className={`mt-1 ${!isBalanceSufficient ? 'text-red-500' : 'text-green-600'}`}
                >
                  Rs. {totalSelectedCharge.toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper className="p-4 rounded-lg border border-gray-100 shadow-sm">
                <Typography variant="subtitle2" color="textSecondary">
                  Remaining Balance
                </Typography>
                <Typography variant="h5" fontWeight="bold" className="mt-1">
                  Rs. {(balance - totalSelectedCharge).toFixed(2)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {!isBalanceSufficient && (
            <Alert severity="error" className="mb-4">
              Selected amount exceeds available balance by Rs. {(totalSelectedCharge - balance).toFixed(2)}
            </Alert>
          )}

          <Divider className="my-4" />

          {data ? (
            <Box className="border border-gray-200 rounded-lg overflow-hidden">
              <AppTable
                columns={columns}
                data={data?.priceItems || []}
                progressPending={isLoading || isFetching}
                paginationServer
                paginationPerPage={perPage}
                paginationTotalRows={data?.totalItems || 0}
                paginationDefaultPage={currentPage}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                selectableRows
                onSelectedRowsChange={handleRowSelected}
                clearSelectedRows={clearSelectedRows}
                selectableRowsHighlight
                noDataComponent={
                  <Box className="p-8 text-center">
                    <CheckCircleOutline className="text-gray-400 text-4xl mb-2" />
                    <Typography variant="body1">No payment items found</Typography>
                    <Typography variant="body2" color="textSecondary">
                      All shipment items are already paid
                    </Typography>
                  </Box>
                }
              />
            </Box>
          ) : (
            <Box display="flex" alignItems="center" justifyContent="center" padding={4}>
              <CircularProgress size={24} />
              <Typography variant="body2" marginLeft={2}>Loading payment items...</Typography>
            </Box>
          )}
        </Box>
      </BreadcrumbLayout.Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.message.includes('exceeds') || snackbar.message.includes('No items') || snackbar.message.includes('Failed') ? 'error' : 'success'}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        aria-labelledby="confirm-transaction-dialog"
        PaperProps={{
          sx: {
            borderRadius: '12px', 
            bgcolor: 'background.paper',
            boxShadow: 3,
          },
        }}
      >
        <DialogTitle
          id="confirm-transaction-dialog"
          sx={{
            borderBottom: 1,
            borderColor: 'divider', 
            py: 2,
            px: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleOutline color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Confirm Prepayment
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <DialogContentText sx={{ mb: 2, color: 'text.primary' }}>
            You're about to apply prepayment to {selectedRows.length} selected items:
          </DialogContentText>
          <Box
            sx={{
              p: 2,
              borderRadius: '8px', 
              mt: 2,
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Paying Amount:
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="body1" fontWeight="bold" color="text.primary">
                  Rs. {totalSelectedCharge.toFixed(2)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Remaining Balance:
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: 'right' }}>
                <Typography variant="body1" fontWeight="bold" color="text.primary">
                  Rs. {(balance - totalSelectedCharge).toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: 1,
            borderColor: 'divider', 
            px: 3,
            py: 2,
          }}
        >
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            color="inherit" 
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          {isBalanceSufficient && (
            <Button
              onClick={handleConfirmTransaction}
              color="primary"
              variant="contained"
              disabled={isUpdating}
              startIcon={isUpdating ? <CircularProgress size={20} /> : null}
            >
              {isUpdating ? 'Processing...' : 'Confirm Prepayment'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PrepaymentShipmentList;